import React, { useState } from "react";
import { Formik, Form } from "formik";
import { Alert, Button, Col, InputGroup, Row } from "react-bootstrap";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";
import styled from "styled-components";

// Helpers
import initialValues from "../../helpers/auth/initialValues";
import validationSchema from "../../helpers/auth/validation";
import * as authActions from "../../store/auth/actions";
import { AuthFormModel } from "../../helpers/auth/formModels";
import { useFieldFocus } from "../../hooks/utility";
import { extractErrorMsg, isIframed } from "../../helpers/utils";
import { themeColors } from "../../helpers/themeColors";

// Assets
import ShippingImg from "../../assets/img/icons/shippingNo.svg";
import UserNameImg from "../../assets/img/icons/username.svg";
import PasswordImg from "../../assets/img/icons/password.svg";

// Components
import { FormikCustomCheckbox, FormikInputGroup } from "../formik/FormikFields";
import SubmitBtn from "../form/SubmitBtn";
import DisabledLoadingOverlay from "../common/DisabledLoadingOverlay";
// import FormikState from "../formik/FormikState";

const colors = themeColors();
const WEBAPPURL = process.env.REACT_APP_WEBAPP_URL;
const MARKETINGSITE = process.env.REACT_APP_MARKETING_SITE_URL;

const PrependIcon = styled(InputGroup.Text)`
  width: 40px;
  justify-content: center;
`;

const ForgotPW = styled.a`
  font-size: 16px;
  line-height: 32px;
  letter-spacing: 0.2px;
  font-weight: 600;
  color: ${colors.darkBlue};
  text-decoration: underline;
  &:hover {
    opacity: 0.7;
    text-decoration: underline;
  }
`;

const RememberMeCol = styled(Col)`
  text-align: center;
  margin-bottom: 0.5rem;
  @media screen and (min-width: 576px) {
    text-align: left;
    margin-bottom: 0;
    & > .form-group {
      margin-left: 0.75rem;
    }
  }
  ${isIframed && `
    @media screen and (min-width: 450px) {
      flex: 0 0 50%;
      max-width: 50%;
      margin-bottom: 0;
    }
  `}
`;

const ForgotPassCol = styled(Col)`
  text-align: center;
  @media screen and (min-width: 576px) {
    text-align: right;
    & > a {
      margin-right: 0.75rem;
    }
  }
  ${isIframed && `
    @media screen and (min-width: 450px) {
      flex: 0 0 50%;
      max-width: 50%;
      margin-bottom: 0;
    }
  `}
`;

const {
  formFields: { keep, shippingNo, username, password },
} = AuthFormModel;

const Login = (props) => {
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [signinError, setSigninError] = useState(null);
  const [resending, setResending] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const [unverifiedAccount, setUnverifiedAccount] = useState(null);

  const focusFieldRef = useFieldFocus();
  const dispatch = useDispatch();

  const handleAfterLogin = () => {
    // If login is made in marketing site, do nothing
    // else re-route to /my-account
    if (!isIframed) {
      document.body.classList.remove("auth-open");
      history.push("/my-account");
    }
  };

  const handleAuthSubmit = async (values) => {
    try {
      setIsLoggingIn(true);
      setUnverifiedAccount(null);
      await dispatch(
        authActions.signin({
          customerId: values.shippingNo,
          username: values.username,
          password: values.password,
          keep: values.keep.toString(),
        })
      );
      handleAfterLogin();
    } catch (error) {
      setIsLoggingIn(false);
      if (
        typeof error.message === "object" &&
        error.message.code &&
        error.message.code === "USER_NOT_CONFIRMED" &&
        error.message.email
      ) {
        const usernameAcctId = `${values.username}${values.shippingNo}`;
        // setUnverifiedAccount(error.message.email);
        setUnverifiedAccount(usernameAcctId);
        setSigninError(error.message.code);
      } else {
        setSigninError(error.message);
      }
    }
  };

  const handleTogglePassword = () => {
    setShowPassword((prevState) => !prevState);
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    if (isIframed) {
      window.parent.postMessage(
        { redirect: `${WEBAPPURL}/reset-password` },
        MARKETINGSITE
      );
    } else {
      history.push("/reset-password");
    }
  };

  const handleResendLinkToEmail = async (e) => {
    e.preventDefault();
    try {
      setResending(true);
      await dispatch(authActions.resendVerificationEmail(unverifiedAccount));
      setEmailResent(true);
    } catch (error) {
      setSigninError(extractErrorMsg(error.message));
    }
    setResending(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleAuthSubmit}
    >
      <Form>
        <div className="py-32px px-4">
          {signinError && (
            <Alert variant="danger" className="alert-soft-danger">
              {signinError === "USER_NOT_CONFIRMED" ? (
                <DisabledLoadingOverlay isLoading={resending}>
                  <p className="text-meta mb-2">
                    Activate your account to finish signing up
                  </p>
                  <p className="text-body-smaller mb-2 text-wrap">
                    We {emailResent ? "re" : ""}sent you a welcome email with an
                    activation link. Simply click and you’re good to go.
                  </p>
                  <p className="text-body-smallest text-right mb-0">
                    <a href="#resend" onClick={handleResendLinkToEmail}>
                      <u>Resend new link!</u>
                    </a>
                  </p>
                </DisabledLoadingOverlay>
              ) : (
                signinError
              )}
            </Alert>
          )}
          <Row>
            <Col xs={12}>
              <FormikInputGroup
                name={shippingNo.Name}
                placeholder={shippingNo.placeholder}
                inputGroupClasses="prepended"
                fieldRef={focusFieldRef}
                prepend={
                  <InputGroup.Prepend>
                    <PrependIcon className="pl-18px pr-1">
                      <img src={ShippingImg} alt="shipping-icon" />
                    </PrependIcon>
                  </InputGroup.Prepend>
                }
                showValidationIconAppend
                size="lg"
                formGroupClasses="mb-4"
              />
            </Col>
            <Col xs={12}>
              <FormikInputGroup
                name={username.Name}
                placeholder={username.placeholder}
                inputGroupClasses="prepended"
                prepend={
                  <InputGroup.Prepend>
                    <PrependIcon className="pl-18px pr-1">
                      <img src={UserNameImg} alt="username-icon" />
                    </PrependIcon>
                  </InputGroup.Prepend>
                }
                size="lg"
                formGroupClasses="mb-4"
                showValidationIconAppend
              />
            </Col>
            <Col xs={12}>
              <FormikInputGroup
                name={password.Name}
                placeholder={password.placeholder}
                inputGroupClasses="prepended appended"
                type={showPassword ? "text" : "password"}
                prepend={
                  <InputGroup.Prepend>
                    <PrependIcon className="pl-18px pr-1">
                      <img
                        className="pre-icons"
                        src={PasswordImg}
                        alt="password-icon"
                      />
                    </PrependIcon>
                  </InputGroup.Prepend>
                }
                append={
                  <InputGroup.Append>
                    <Button
                      variant="link"
                      onClick={handleTogglePassword}
                      className="text-big bolder text-secondary px-18px"
                    >
                      <i
                        className={`fe ${
                          showPassword ? "fe-eye-off" : "fe-eye"
                        }`}
                      />
                    </Button>
                  </InputGroup.Append>
                }
                size="lg"
                formGroupClasses="mb-4"
                showValidationIconAppend
                helperText={<><span className="fe fe-info mr-2"/>Password is case-sensitive.</>}
              />
            </Col>
          </Row>
          <Row className="align-items-center">
            <RememberMeCol sm={6}>
              <FormikCustomCheckbox
                name={keep.Name}
                label={keep.label}
                formGroupClasses="mb-0"
                labelClasses="d-flex"
                style={{ lineHeight: "32px" }}
              />
            </RememberMeCol>
            <ForgotPassCol sm={6} className="forgot-password-link">
              <ForgotPW
                href="#forgotPass"
                onClick={handleResetPassword}
                className="my-auto"
              >
                Forgot Password
              </ForgotPW>
            </ForgotPassCol>
          </Row>
        </div>
        <SubmitBtn block isSubmitting={isLoggingIn}>
          Sign in
        </SubmitBtn>
        {/* <FormikState /> */}
      </Form>
    </Formik>
  );
};

export default Login;
